import React, { createContext, useContext, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";

const AuthContext = createContext(null);

// Detect OAuth callback fragment synchronously. We don't fetch /me until
// the callback page has exchanged session_id for a cookie.
const isReturningFromOAuth = () =>
  typeof window !== "undefined" && window.location.hash?.includes("session_id=");

export function AuthProvider({ children }) {
  const qc = useQueryClient();
  const skipFetch = isReturningFromOAuth();

  const { data: user, isLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => endpoints.me().catch(() => null),
    enabled: !skipFetch,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const setUser = useCallback(
    (next) => qc.setQueryData(["auth", "me"], next),
    [qc]
  );

  const logout = useCallback(async () => {
    await endpoints.logout();
    qc.setQueryData(["auth", "me"], null);
  }, [qc]);

  const checkAuth = useCallback(
    () => qc.invalidateQueries({ queryKey: ["auth", "me"] }),
    [qc]
  );

  const value = useMemo(
    () => ({
      user: user ?? null,
      setUser,
      loading: !skipFetch && isLoading,
      logout,
      checkAuth,
    }),
    [user, setUser, isLoading, skipFetch, logout, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
